import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../api';

const AuraDeviceManagementPage = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [deviceName, setDeviceName] = useState('');
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [newToken, setNewToken] = useState(null);
    const [error, setError] = useState(null);
    const [pairingCode, setPairingCode] = useState('');
    const [pairingStatus, setPairingStatus] = useState(null);

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/api/v1/aura-devices/tokens');
            setDevices(response.data.devices || []);
        } catch (err) {
            console.error('Error fetching devices:', err);
            setError(err?.response?.data?.detail || 'Failed to fetch devices');
        } finally {
            setLoading(false);
        }
    };

    const generateToken = async () => {
        try {
            setGenerating(true);
            setError(null);
            const response = await api.post('/api/v1/aura-devices/generate-token', {
                device_name: deviceName || undefined
            });
            setNewToken(response.data);
            setShowTokenModal(true);
            setDeviceName('');
            fetchDevices(); // Refresh list
        } catch (err) {
            console.error('Error generating token:', err);
            setError(err?.response?.data?.detail || 'Failed to generate device token. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    const handlePairDevice = async () => {
        if (pairingCode.length !== 6) {
            setError('Please enter a 6-digit pairing code');
            return;
        }

        try {
            setGenerating(true);
            setError(null);
            const response = await api.post('/api/v1/aura-devices/complete-pairing', {
                pairing_code: pairingCode,
                device_name: deviceName || undefined
            });
            
            if (response.data.success) {
                setPairingStatus('success');
                setPairingCode('');
                setDeviceName('');
                fetchDevices(); // Refresh list
                setTimeout(() => setPairingStatus(null), 3000);
            }
        } catch (err) {
            console.error('Error pairing device:', err);
            setError(err?.response?.data?.detail || 'Failed to pair device. Please check the pairing code and try again.');
            setPairingStatus('error');
        } finally {
            setGenerating(false);
        }
    };

    const revokeToken = async (tokenId) => {
        if (!window.confirm('Are you sure you want to revoke this device token? The device will be disconnected.')) {
            return;
        }

        try {
            await api.delete(`/api/v1/aura-devices/tokens/${tokenId}`);
            fetchDevices(); // Refresh list
        } catch (err) {
            console.error('Error revoking token:', err);
            alert(err?.response?.data?.detail || 'Failed to revoke device token. Please try again.');
        }
    };

    const copyToken = () => {
        if (newToken) {
            navigator.clipboard.writeText(newToken.device_token);
            alert('Device token copied to clipboard!');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <main className="flex-1 p-6 lg:p-8">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">AURA Device Management</h1>
                        <p className="text-gray-600">Connect and manage your AURA voice assistant devices</p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                            {error}
                        </div>
                    )}

                    {pairingStatus === 'success' && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
                            ✅ Device paired successfully!
                        </div>
                    )}

                    {/* Pair New Device Section (New - Recommended) */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold mb-2">✨ Pair New Device (Recommended)</h2>
                        <p className="text-gray-600 mb-4">
                            Enter the 6-digit pairing code shown on your AURA device. The device will automatically receive the connection token.
                        </p>
                        <div className="flex gap-4">
                            <input
                                type="text"
                                placeholder="Enter 6-digit code (e.g., 123456)"
                                value={pairingCode}
                                onChange={(e) => setPairingCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-2xl text-center tracking-widest font-mono"
                                maxLength={6}
                            />
                            <button
                                onClick={handlePairDevice}
                                disabled={pairingCode.length !== 6 || generating}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {generating ? 'Pairing...' : 'Pair Device'}
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            💡 Your AURA device should be showing a pairing code. Enter it above to connect.
                        </p>
                    </div>

                    {/* Generate New Token Section (Legacy Method) */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold mb-4">Connect New Device</h2>
                        <p className="text-gray-600 mb-4">
                            Generate a device token to connect your AURA device. The token will be used to authenticate your device.
                        </p>
                        <div className="flex gap-4">
                            <input
                                type="text"
                                placeholder="Device name (optional, e.g., 'Living Room AURA')"
                                value={deviceName}
                                onChange={(e) => setDeviceName(e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                                onClick={generateToken}
                                disabled={generating}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {generating ? 'Generating...' : 'Generate Token'}
                            </button>
                        </div>
                    </div>

                    {/* Devices List */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4">Connected Devices</h2>
                        {loading ? (
                            <p className="text-gray-500">Loading devices...</p>
                        ) : devices.length === 0 ? (
                            <p className="text-gray-500">No devices connected yet. Generate a token above to connect your first device.</p>
                        ) : (
                            <div className="space-y-4">
                                {devices.map((device) => (
                                    <div
                                        key={device.id}
                                        className="border border-gray-200 rounded-lg p-4 flex justify-between items-start"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-semibold text-lg">
                                                    {device.device_name || `Device ${device.id}`}
                                                </h3>
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                                        device.is_active
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}
                                                >
                                                    {device.is_active ? 'Active' : 'Revoked'}
                                                </span>
                                            </div>
                                            <div className="text-sm text-gray-600 space-y-1">
                                                <p>
                                                    <span className="font-medium">Token:</span>{' '}
                                                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                                        {device.device_token.substring(0, 20)}...
                                                    </code>
                                                </p>
                                                <p>
                                                    <span className="font-medium">Created:</span>{' '}
                                                    {formatDate(device.created_at)}
                                                </p>
                                                <p>
                                                    <span className="font-medium">Last Used:</span>{' '}
                                                    {formatDate(device.last_used_at)}
                                                </p>
                                                {device.expires_at && (
                                                    <p>
                                                        <span className="font-medium">Expires:</span>{' '}
                                                        {formatDate(device.expires_at)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {device.is_active && (
                                            <button
                                                onClick={() => revokeToken(device.id)}
                                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Token Modal */}
            {showTokenModal && newToken && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4">
                        <h2 className="text-2xl font-bold mb-4">Device Token Generated!</h2>
                        <p className="text-gray-600 mb-4">
                            Use this token to connect your AURA device. <strong>Copy it now</strong> - you won't be able to see it again after closing this dialog!
                        </p>
                        <div className="bg-gray-100 rounded-lg p-4 mb-4">
                            <code className="text-sm break-all font-mono">{newToken.device_token}</code>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                            <p className="text-sm text-yellow-800">
                                <strong>Important:</strong> Save this token securely. You'll need to enter it in your AURA device application to connect.
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={copyToken}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Copy Token
                            </button>
                            <button
                                onClick={() => {
                                    setShowTokenModal(false);
                                    setNewToken(null);
                                }}
                                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AuraDeviceManagementPage;
